import { PieChart } from "lucide-react"
import { Card } from "../../ui"
import { formatAmount } from "../../constants"
import { getApiErrorMessage } from "../../orderSubmitFeedback"
import { HScroll } from "procurement/design/components/HScroll"

const STATUS_STYLE = {
  OVER_BUDGET: { color: "var(--pc-danger, #b42318)" },
  UNALLOCATED: { color: "var(--pc-warn, #b54708)" },
}

const money = (v) => (v == null ? "—" : formatAmount(v))

// Unsaved order form: rows from POST /order-requests/budget-preview.
// `lineInfo[i]` = { cardNo, itemName, centerName } for request line i; a row
// covers `lineIndexes` (several only on the "Chưa phân phối" row).
// Bucket figures repeat across lines of the same bucket, so only `amount` is summed.
// `uncommitted` (default): the order isn't recorded in the budget yet, so BE's
// "Remaining after" (remaining minus this order) is shown. Once approved the
// commit is already in the figures, so that column is hidden.
// "Chưa phân phối" rows collapse into one per center: amounts add up, and the lines, items and
// months it covers are merged. Funded months stay as they come.
const mergeUnallocated = (rows, lineInfo) => {
  const out = []
  const byCenter = new Map()
  rows.forEach((r) => {
    if (r.period?.budgetStatus !== "UNALLOCATED" && r.period?.key !== "UNALLOCATED") {
      out.push(r)
      return
    }
    const id = r.budgetCenterId ?? r.centerId
    const items = (r.lineIndexes || []).map((i) => lineInfo[i]?.itemName).filter(Boolean)
    const prev = byCenter.get(id)
    if (!prev) {
      const row = { ...r, lineIndexes: [...(r.lineIndexes || [])], itemNames: [...new Set(items)] }
      byCenter.set(id, row)
      out.push(row)
      return
    }
    prev.amount = (Number(prev.amount) || 0) + (Number(r.amount) || 0)
    prev.lineIndexes.push(...(r.lineIndexes || []))
    items.forEach((n) => !prev.itemNames.includes(n) && prev.itemNames.push(n))
    prev.unallocatedMonths = [
      ...new Set(`${prev.unallocatedMonths || ""}, ${r.unallocatedMonths || ""}`.split(",").map((m) => m.trim()).filter(Boolean)),
    ].join(", ")
  })
  return out
}

const OrderBudgetAllocation = ({ rows: rawRows = [], lineInfo = [], loading, error, uncommitted = true }) => {
  const rows = mergeUnallocated(rawRows, lineInfo)
  return (
  <Card
    icon={<PieChart aria-hidden />}
    title="Budget allocation"
    subtitle="How each line splits across the Order-for months and budget buckets."
  >
    {error ? (
      <p className="pc-muted" style={{ color: "var(--pc-danger, #b42318)" }}>
        {getApiErrorMessage(error, "Couldn't preview the budget allocation.")}
      </p>
    ) : loading && rows.length === 0 ? (
      <p className="pc-muted">Loading budget allocation…</p>
    ) : rows.length === 0 ? (
      <p className="pc-muted">Pick a category and add lines with a center to see the allocation.</p>
    ) : (
      <HScroll className="pc-table-wrap">
        <table className="pc-table">
          <thead>
            <tr>
              <th>Line</th>
              <th>Center</th>
              <th>Item</th>
              <th>Month</th>
              <th className="pc-th-right">Amount</th>
              <th className="pc-th-right">Committed</th>
              <th className="pc-th-right">Budget available</th>
              {uncommitted && <th className="pc-th-right">Remaining after</th>}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const indexes = r.lineIndexes || []
              const info = lineInfo[indexes[0]] || {}
              const cards = [...new Set(indexes.map((i) => lineInfo[i]?.cardNo).filter(Boolean))]
              return (
                <tr key={`${indexes.join(",")}:${r.period?.key}`}>
                  <td>{cards.map((n) => `#${n}`).join(", ")}</td>
                  <td className="pc-td-strong">{info.centerName || `Center #${r.centerId}`}</td>
                  <td title={r.itemNames?.length > 1 ? r.itemNames.join(", ") : undefined}>
                    {r.itemNames?.length > 1 ? `${r.itemNames.length} items` : r.itemNames?.[0] || info.itemName || "—"}
                  </td>
                  <td
                    title={r.unallocatedMonths || undefined}
                    style={{ whiteSpace: "nowrap", ...STATUS_STYLE[r.period?.budgetStatus] }}
                  >
                    {r.period?.label || "—"}
                  </td>
                  <td className="pc-td-right">{money(r.amount)}</td>
                  <td className="pc-td-right">{money(r.committed)}</td>
                  <td className="pc-td-right">{money(r.remaining)}</td>
                  {uncommitted && (
                    <td
                      className="pc-td-right"
                      style={r.remainingAfter < 0 ? { color: "var(--pc-danger, #b42318)" } : undefined}
                    >
                      {money(r.remainingAfter)}
                    </td>
                  )}
                </tr>
              )
            })}
            <tr>
              <td className="pc-td-strong" colSpan={4}>
                Total
              </td>
              <td className="pc-td-right pc-td-strong">
                {formatAmount(rows.reduce((s, r) => s + (Number(r.amount) || 0), 0))}
              </td>
              <td colSpan={uncommitted ? 3 : 2} />
            </tr>
          </tbody>
        </table>
      </HScroll>
    )}
  </Card>
  )
}

export default OrderBudgetAllocation
